import { type IEventEmitter } from '../classes';
import { CollectionResource, ItemResource } from './resource';
import { ApiError } from './error';
import { type IRequestConfig } from 'angular';

export enum Events {
    Loaded = 'loaded',
    Updated = 'updated',
    Removed = 'removed',
    Fail = 'fail',
    Started = 'started'
}

export enum ActionTypes {
    Add,
    Update,
    Remove
}

type ActionType = ActionTypes.Add | ActionTypes.Update | ActionTypes.Remove;

export interface IDetail {
    localizedMessage?: string;
    message?: string;
    code?: string;
}

export interface IRequestResult<Data = any> extends Record<string, any> {
    details?: IDetail[];
    data?: Data;
    message?: string;
    localizedMessage?: string;
    exception?: string;
    code?: string;
}

export interface IPartialItem {
    [others: string]: any;
}

export type PartialType<T> = {
    [K in keyof T]?: T[K];
};

export interface IItem extends IPartialItem {
    id?: number | string;
}

export interface IList<Item extends IItem> {
    items: Item[];
    page: IPagination | null;
}

export interface IResourceParams extends Record<string, any> {
    id?: number | string;
    [others: string]: any;
}

export interface ICollectionParams extends Record<string, any> {
    pagination?: IPagination;
    filters?: IFilter;
    sorting?: ISorting[];
    selection?: any;
}

export interface IFilter extends Record<string, any> {
    [others: string]: any;
}

export interface IPagination {
    number: number;
    size: number;
    totalElements?: number;
    totalPages?: number;
}

export interface ISorting {
    field: string;
    direction: string;
    sortField?: string;
    priority?: number;
}

export interface IActiveSorting {
    field: string;
    displayName?: string;
    direction: string;
    priority?: number;
    hidden?: boolean;
    name?: string;
    sortField?: string;
}

export interface IAction {
    type: ActionType;
    hash: string;
}

export interface IResourceEvent {
    forResource: string;
    action: IAction;
}

export interface IBaseDataProvider extends IEventEmitter {
    list(collectionParams?: ICollectionParams, resourceParams?: IResourceParams, cancel?: Promise<void>);
    get(resourceParams?: object, params?: object, cancel?: Promise<void>);
    update(item: any, resourceParams?: IResourceParams, cancel?: Promise<void>, stealth?: boolean);
    add(item: any, resourceParams?: IResourceParams, cancel?: Promise<void>, stealth?: boolean);
    remove(resourceParams?: IResourceParams, cancel?: Promise<void>, stealth?: boolean): Promise<void>;
    getCollectionResource(resourceParams?: IResourceParams, debug?: boolean);
    getItemResource(resourceParams: IResourceParams);
    isError(error: ApiError, name: string): boolean;
    getErrorMessage(error: ApiError, name: string): string;
    getPageNames(collectionParams: ICollectionParams, resourceParams: IResourceParams, light?: boolean, cancel?: Promise<void>);
}

/**
 * @deprecated
 */
export interface IDataProvider<Item extends IItem> extends IBaseDataProvider {
    list(collectionParams?: ICollectionParams, resourceParams?: IResourceParams, cancel?: Promise<void>): Promise<IList<Item>>;
    get(resourceParams?: object, params?: object, cancel?: Promise<void>): Promise<Item>;
    update(item: IPartialItem, resourceParams?: IResourceParams, cancel?: Promise<void>, stealth?: boolean): Promise<Item>;
    patch(partialItem: IPartialItem, resourceParams?: IResourceParams, cancel?: Promise<void>, stealth?: boolean): Promise<Item>;
    add(item: Item, resourceParams?: IResourceParams, cancel?: Promise<void>, stealth?: boolean): Promise<Item>;
    remove(resourceParams?: IResourceParams, cancel?: Promise<void>, stealth?: boolean): Promise<any>;
    getCollectionResource(resourceParams?: IResourceParams, debug?: boolean): CollectionResource<Item>;
    getItemResource(resourceParams: IResourceParams): ItemResource<Item>;
    get apiUrl(): string;
}

export interface IDataProviderV2<
    ListObject = IItem,
    InfoObject = IItem,
    AddObject = object,
    UpdateObject = object,
    PatchObject = object,
    Params extends IResourceParams = object
> extends IBaseDataProvider {
    list(collectionParams?: ICollectionParams, resourceParams?: Params, cancel?: Promise<void> | number): Promise<IList<ListObject>>;
    all(collectionParams?: ICollectionParams, resourceParams?: Params, cancel?: Promise<void>): Promise<ListObject[]>;
    get(resourceParams?: Params, params?: object, cancel?: Promise<void>): Promise<InfoObject>;
    update(item: UpdateObject, resourceParams?: Params, cancel?: Promise<void>, stealth?: boolean): Promise<InfoObject>;
    patch(partialItem: Partial<PatchObject>, resourceParams?: Params, cancel?: Promise<void>, stealth?: boolean): Promise<InfoObject>;
    customPatch(partialItem: Partial<UpdateObject>, resourceParams?: Params, cancel?: Promise<void>, stealth?: boolean): Promise<InfoObject>;
    add(item: AddObject, resourceParams?: Params, cancel?: Promise<void>, stealth?: boolean): Promise<InfoObject>;
    remove(resourceParams?: Params, cancel?: Promise<void>, stealth?: boolean): Promise<void>;
    getCollectionResource(resourceParams?: Params, debug?: boolean): CollectionResource<ListObject>;
    getItemResource(resourceParams: Params): ItemResource<InfoObject>;
    get apiUrl(): string;
}

export interface IHttpDataService {
    getList<ReturnType, Params = unknown>(url: string, params?: Params, cancel?: Promise<void>): Promise<IList<ReturnType>>;
    getItem<ReturnType, Params = unknown>(url: string, params?: Params, cancel?: Promise<void>): Promise<ReturnType>;
    updateRequest<ReturnType, Data = unknown, Params = unknown>(
        url: string,
        method: HttpRequestMethod,
        data?: Data,
        params?: Params,
        cancel?: Promise<void>
    ): Promise<ReturnType>;
    updateRequestFormData<ReturnType, Params = unknown>(
        url: string,
        method: HttpRequestMethod,
        data: FormData,
        params?: Params,
        cancel?: Promise<void>
    ): Promise<ReturnType>;
    handleError<Response = unknown>(result: angular.IHttpResponse<Response>, config: IRequestConfig): Error;
    isError(error: ApiError, code: string | string[]): boolean;
    getErrorMessage(error: ApiError, name: string): string;
    // @deprecated
    http: angular.IHttpService;
}

export interface IDataProviderHelper {
    createRequestParams<Params extends ICollectionParams>(collectionParams: Params): any;
    addSortingParameters<Params extends Record<string, any>>(params: Params, sorting: ISorting[]): void;
    addPaginationParameters<Params extends Record<string, any>>(params: Params, pagination: IPagination): void;
    addFilterParameters<Params extends Record<string, any>>(params: Params, filter: IFilter): void;
    /**
     * takes the masked url and injects the params in the path string.
     *
     * @example params: {id: number} path: `client/{id}` param: { client: 90 } will return `client/90`
     * @param urlMask where the params should get injected; place holder is `{}` @example `client/{id}` id would be the placeholder
     * @param resourceParams that should get injected.
     */
    buildUrl<Params extends IResourceParams>(urlMask: string, resourceParams: Params): string;
    urlToPath(url: string): string;
}

type CaseInsensitive<Ref extends string> = Lowercase<Ref> | Uppercase<Ref>;
type HttpBaseUpdateMethod = 'PUT' | 'PATCH';
type HttpBaseFetchMethod = 'GET';
type HttpBaseDeleteMethod = 'DELETE';
type HttpBaseCreateMethod = 'POST';
export type HttpUpdateMethod = CaseInsensitive<HttpBaseUpdateMethod>;
export type HttpFetchMethod = CaseInsensitive<HttpBaseFetchMethod>;
export type HttpDeleteMethod = CaseInsensitive<HttpBaseDeleteMethod>;
export type HttpCreateMethod = CaseInsensitive<HttpBaseCreateMethod>;
export type HttpRequestMethod = HttpUpdateMethod | HttpFetchMethod | HttpDeleteMethod | HttpCreateMethod;
